import { useState, useCallback, useMemo } from 'react';
import { Button, Menu, Text, Group, Badge, TextInput } from '@mantine/core';
import { HiOutlineShare } from 'react-icons/hi';
import { IWorkerJobData } from '@th-types/workerProfile.type';
import { getShareButtonConfig } from '@company/utils/worker-modal-helper';
import { useShareJobMutation } from '@company/hooks/useShareJobMutation';
import { useActiveJobsList } from '@company/hooks/useJobList';

interface ShareButtonProps {
  workerData: IWorkerJobData;
  jobId?: number;
  showSuccessAlert: (message: string) => void;
  showWarningAlert: (message: string) => void;
}

export default function ShareButton({
  workerData,
  jobId,
  showSuccessAlert,
  showWarningAlert,
}: ShareButtonProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const shareJobMutation = useShareJobMutation();
  const { getShareableJobs, isLoading: isLoadingJobs } = useActiveJobsList(
    workerData.worker.id
  );

  const shareJobToWorker = useCallback(
    async (targetJobId: number) => {
      const { id: workerId } = workerData?.worker || {};
      shareJobMutation.mutate({
        jobId: targetJobId,
        workerId,
        showSuccessAlert,
        showWarningAlert,
        onSuccess: () => {
          console.log(
            'ShareButton: Custom onSuccess callback called, closing dropdown'
          );
          setIsDropdownOpen(false);
        },
      });
    },
    [workerData?.worker, shareJobMutation, showSuccessAlert, showWarningAlert]
  );

  const handleDropdownToggle = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
    // Reset filter when opening dropdown
    if (!isDropdownOpen) {
      setFilterValue('');
    }
  }, [isDropdownOpen]);

  const shareConfig = getShareButtonConfig(workerData, jobId);

  // Get dropdown items for filtering (only when needed)
  const dropdownItems =
    shareConfig.type === 'dropdown' ? getShareableJobs(shareJobToWorker) : null;
  const isDropdownEmpty = dropdownItems && 'label' in dropdownItems;

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    const items = Array.isArray(dropdownItems) ? dropdownItems : [];

    if (!filterValue.trim() || isDropdownEmpty) {
      return items;
    }

    const searchTerm = filterValue.toLowerCase();
    const result: typeof items = [];
    let currentEventGroup: typeof items = [];
    let hasMatchingItemsInCurrentGroup = false;

    items.forEach((item, index) => {
      const isHeaderItem = item.header && !item.label;
      const isPositionItem = !item.header && item.label;

      if (isHeaderItem) {
        // If we have a previous group with matching items, add it to results
        if (hasMatchingItemsInCurrentGroup && currentEventGroup.length > 0) {
          result.push(...currentEventGroup);
        }

        // Start a new group
        currentEventGroup = [item];
        hasMatchingItemsInCurrentGroup = false;
      } else if (isPositionItem) {
        // Check if this position matches the search term
        const labelMatch = item.label.toLowerCase().includes(searchTerm);
        const headerMatch =
          currentEventGroup[0]?.header?.toLowerCase().includes(searchTerm) ||
          false;

        if (labelMatch || headerMatch) {
          currentEventGroup.push(item);
          hasMatchingItemsInCurrentGroup = true;
        }
      }

      // If this is the last item, add the current group if it has matches
      if (
        index === items.length - 1 &&
        hasMatchingItemsInCurrentGroup &&
        currentEventGroup.length > 0
      ) {
        result.push(...currentEventGroup);
      }
    });

    return result;
  }, [dropdownItems, filterValue, isDropdownEmpty]);

  // Don't render if share button should not be shown
  if (!shareConfig.showButton) {
    return null;
  }

  // Job-specific context: Simple button
  if (shareConfig.type === 'specific') {
    return (
      <Button
        size="sm"
        variant="filled"
        color="primary"
        onClick={() => shareJobToWorker(shareConfig.jobId!)}
        leftSection={<HiOutlineShare size={20} />}
        loading={shareJobMutation.isPending}
      >
        {shareConfig.buttonText}
      </Button>
    );
  }

  // General context: Dropdown button
  if (shareConfig.type === 'dropdown') {
    return (
      <Menu
        opened={isDropdownOpen}
        onClose={() => setIsDropdownOpen(false)}
        position="bottom-end"
        shadow="md"
      >
        <Menu.Target>
          <Button
            size="sm"
            variant="filled"
            color="primary"
            leftSection={<HiOutlineShare size={20} />}
            onClick={handleDropdownToggle}
            loading={shareJobMutation.isPending || isLoadingJobs}
          >
            {shareConfig.buttonText}
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          {isDropdownEmpty ? (
            <Menu.Item disabled>
              <Text size="sm" c="dimmed">
                {dropdownItems.label}
              </Text>
            </Menu.Item>
          ) : (
            <>
              {/* Filter input */}
              <div
                style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--mantine-color-gray-3)',
                }}
              >
                <TextInput
                  placeholder="Type to filter jobs..."
                  value={filterValue}
                  onChange={(event) =>
                    setFilterValue(event.currentTarget.value)
                  }
                  size="xs"
                  styles={{
                    input: {
                      fontSize: '12px',
                    },
                  }}
                />
              </div>

              {/* Filtered items */}
              {filteredItems.length === 0 ? (
                <Menu.Item disabled>
                  <Text size="sm" c="dimmed">
                    No jobs match your search
                  </Text>
                </Menu.Item>
              ) : (
                filteredItems.map((item) => {
                  // Handle header-only items (job/event headers)
                  if (item.header && !item.label) {
                    return (
                      <Menu.Item
                        key={item.key}
                        disabled
                        c="black"
                        style={{ cursor: 'default' }}
                      >
                        <Text size="xs" style={{ padding: '4px 0' }}>
                          {item.header}
                        </Text>
                      </Menu.Item>
                    );
                  }

                  // Handle position items
                  return (
                    <Menu.Item
                      key={item.key}
                      onClick={item.action || undefined}
                      disabled={!item.action}
                    >
                      <Group justify="space-between" align="center">
                        <Text
                          size="sm"
                          style={{ flex: 1, paddingLeft: '12px' }}
                        >
                          {item.label}
                        </Text>
                        {item.tag && (
                          <Badge
                            size="xs"
                            color={
                              item.tag.className.includes('primary')
                                ? 'yellow'
                                : 'green'
                            }
                            variant="light"
                          >
                            {item.tag.status}
                          </Badge>
                        )}
                      </Group>
                    </Menu.Item>
                  );
                })
              )}
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  }

  return null;
}
